import React, { useState, useEffect } from 'react';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { socialMediaService, UploadMetadata } from '../../../shared/services/socialMediaService';
import { metadataGeneratorService } from '../../../shared/services/metadataGeneratorService';
import { videoHighlightsService } from '../../../shared/services/videoHighlightsService';
import { videosService } from '../../../shared/services/videosService';
import { supabase } from '../../../shared/lib/supabase';
import Modal from '../../../shared/components/ui/Modal';
import Toast from '../../../shared/components/ui/Toast';

interface UploadToSocialMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  entityType: 'highlight' | 'video';
  onSuccess?: () => void;
}

// Platform Icons Components
const YouTubeIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const InstagramIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TikTokIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FacebookIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TwitterIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const PLATFORMS = [
  { id: 'youtube', name: 'YouTube', icon: YouTubeIcon, enabled: true },
  { id: 'instagram', name: 'Instagram', icon: InstagramIcon, enabled: false },
  { id: 'tiktok', name: 'TikTok', icon: TikTokIcon, enabled: false },
  { id: 'facebook', name: 'Facebook', icon: FacebookIcon, enabled: false },
  { id: 'twitter', name: 'Twitter/X', icon: TwitterIcon, enabled: false },
] as const;

const UploadToSocialMediaModal: React.FC<UploadToSocialMediaModalProps> = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  onSuccess,
}) => {
  const { currentOrganization } = useOrg();
  const orgIdFromPath = extractOrgIdFromPath(window.location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || 'default';

  const [selectedPlatform, setSelectedPlatform] = useState<string>('youtube');
  const [videoType, setVideoType] = useState<'stitched' | 'enhanced'>('stitched');
  const [availableVideoTypes, setAvailableVideoTypes] = useState<{ stitched: boolean; enhanced: boolean }>({
    stitched: false,
    enhanced: false,
  });
  const [isShort, setIsShort] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<UploadMetadata>({
    title: '',
    description: '',
    tags: [],
    privacyStatus: 'public',
  });
  const [tagsInput, setTagsInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [orgAccounts, setOrgAccounts] = useState<any[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [toast, setToast] = useState<{ isVisible: boolean; type: 'success' | 'error' | 'info'; message: string }>({
    isVisible: false,
    type: 'info',
    message: '',
  });

  // Load org accounts when modal opens
  useEffect(() => {
    if (isOpen && selectedPlatform === 'youtube') {
      loadOrgAccounts();
    }
  }, [isOpen, selectedPlatform, orgId]);

  // Check available video types (for highlights) and generate default metadata when modal opens
  useEffect(() => {
    if (isOpen) {
      if (entityType === 'highlight') {
        checkAvailableVideoTypes();
      }
      generateDefaultMetadata();
    }
  }, [isOpen, entityId, entityType]);

  const loadOrgAccounts = async () => {
    if (!orgId) return;

    setIsLoadingAccounts(true);
    try {
      const result = await socialMediaService.getOrgIntegrations(orgId, selectedPlatform as any);
      if (result.success && result.data && result.data.length > 0) {
        setOrgAccounts(result.data);
        setSelectedAccountId(result.data[0].id);
      } else {
        // No org-level account: use parent-level (Kridah Labs) fallback if available
        const fallbackResult = await socialMediaService.getIntegrationForUpload(
          orgId,
          selectedPlatform as 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'twitter'
        );
        if (fallbackResult.success && fallbackResult.data) {
          setOrgAccounts([fallbackResult.data]);
          setSelectedAccountId(fallbackResult.data.id);
        } else {
          setOrgAccounts([]);
          setSelectedAccountId('');
        }
      }
    } catch (error) {
      console.error('Error loading org accounts:', error);
      setOrgAccounts([]);
      setSelectedAccountId('');
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const checkAvailableVideoTypes = async () => {
    if (entityType !== 'highlight') return;

    try {
      const highlightResult = await videoHighlightsService.getHighlightById(entityId);
      if (highlightResult.success && highlightResult.data) {
        const highlight = highlightResult.data;
        setAvailableVideoTypes({
          stitched: !!highlight.output_storage_path,
          enhanced: !!highlight.enhanced_storage_path,
        });
        // Auto-select enhanced if available, otherwise stitched
        if (highlight.enhanced_storage_path) {
          setVideoType('enhanced');
        } else if (highlight.output_storage_path) {
          setVideoType('stitched');
        }
      }
    } catch (error) {
      console.error('Error checking available video types:', error);
    }
  };

  const generateDefaultMetadata = async () => {
    setIsGenerating(true);
    try {
      let result;
      
      if (entityType === 'highlight') {
        result = await metadataGeneratorService.generateMetadata(
          entityId,
          selectedPlatform as any
        );
      } else if (entityType === 'video') {
        result = await metadataGeneratorService.generateMetadataForVideo(
          entityId,
          selectedPlatform as any
        );
      } else {
        setIsGenerating(false);
        return;
      }

      if (result.success && result.data) {
        // Apply isShort settings if needed
        let generatedMetadata = result.data;
        if (isShort && selectedPlatform === 'youtube') {
          generatedMetadata = {
            ...generatedMetadata,
            categoryId: null,
            isShort: true,
            title: generatedMetadata.title.startsWith('#Shorts') 
              ? generatedMetadata.title 
              : `#Shorts ${generatedMetadata.title}`
          };
        }
        setMetadata(generatedMetadata);
        setTagsInput(generatedMetadata.tags?.join(', ') || '');
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to generate metadata',
        });
      }
    } catch (error) {
      console.error('Error generating metadata:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to generate metadata',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePlatformChange = (platform: string) => {
    const platformData = PLATFORMS.find(p => p.id === platform);
    if (!platformData?.enabled) return; // Don't allow selection of disabled platforms
    
    setSelectedPlatform(platform);
    // Reload accounts for the new platform
    if (platform === 'youtube' && orgId) {
      loadOrgAccounts();
    }
    // Generate metadata for both highlights and videos
    generateDefaultMetadata();
  };

  const handleTagsChange = (value: string) => {
    setTagsInput(value);
    const tags = value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    setMetadata(prev => ({ ...prev, tags }));
  };

  const handleSubmit = async () => {
    if (!metadata.title.trim()) {
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Title is required',
      });
      return;
    }

    setIsLoading(true);
    try {
      // Get integration - use selected account if org has accounts, otherwise use fallback logic
      let integration;
      
      if (orgAccounts.length > 0 && selectedAccountId) {
        // Use selected org account
        const selectedAccount = orgAccounts.find(acc => acc.id === selectedAccountId);
        if (!selectedAccount) {
          setToast({
            isVisible: true,
            type: 'error',
            message: 'Selected account not found',
          });
          setIsLoading(false);
          return;
        }
        integration = selectedAccount;
      } else {
        // Fallback to getIntegrationForUpload (will use parent if no org accounts)
        const integrationResult = await socialMediaService.getIntegrationForUpload(
          orgId,
          selectedPlatform as any
        );

        if (!integrationResult.success || !integrationResult.data) {
          setToast({
            isVisible: true,
            type: 'error',
            message: integrationResult.error || 'No integration found for this platform',
          });
          setIsLoading(false);
          return;
        }

        integration = integrationResult.data;
      }

      // Get match ID based on entity type
      let matchId: string = '';
      if (entityType === 'highlight') {
        const highlightResult = await videoHighlightsService.getHighlightById(entityId);
        if (highlightResult.success && highlightResult.data) {
          matchId = highlightResult.data.match_id;
        } else {
          setToast({
            isVisible: true,
            type: 'error',
            message: 'Highlight not found',
          });
          setIsLoading(false);
          return;
        }
      } else if (entityType === 'video') {
        // Check match_videos table first
        const { data: matchVideos, error: matchVideosError } = await supabase
          .from('match_videos')
          .select('match_id')
          .eq('storage_video_id', entityId)
          .limit(1);

        if (!matchVideosError && matchVideos && matchVideos.length > 0) {
          matchId = matchVideos[0].match_id;
        } else {
          // Fallback to processing_notes
          const videoResult = await videosService.getVideoById(entityId);
          if (videoResult.success && videoResult.data) {
            const processingNotes = videoResult.data.processing_notes;
            if (processingNotes && typeof processingNotes === 'object') {
              matchId = (processingNotes as Record<string, any>).match_id || '';
            }
          } else {
            setToast({
              isVisible: true,
              type: 'error',
              message: 'Video not found',
            });
            setIsLoading(false);
            return;
          }
        }
      }

      // Ensure metadata has isShort flag and correct categoryId for YouTube
      const finalMetadata: UploadMetadata = {
        ...metadata,
        isShort: selectedPlatform === 'youtube' ? isShort : undefined,
        categoryId: selectedPlatform === 'youtube' && isShort ? null : metadata.categoryId
      };

      const result = await socialMediaService.createUploadRecord(
        entityId,
        entityType,
        matchId || null,
        orgId,
        integration.id,
        selectedPlatform,
        finalMetadata,
        entityType === 'highlight' ? videoType : undefined
      );

      if (result.success && result.data) {
        setToast({
          isVisible: true,
          type: 'success',
          message: 'Upload initiated successfully',
        });
        onSuccess?.();
        setTimeout(() => {
          onClose();
          resetForm();
        }, 1500);
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to create upload',
        });
      }
    } catch (error) {
      console.error('Error creating upload:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to create upload',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedPlatform('youtube');
    setVideoType('stitched');
    setIsShort(false);
    setMetadata({
      title: '',
      description: '',
      tags: [],
      privacyStatus: 'public',
    });
    setTagsInput('');
    setOrgAccounts([]);
    setSelectedAccountId('');
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Upload to Social Media"
      >
        <div className="flex flex-col">
          {/* Scrollable Content */}
          <div className="p-6 space-y-6">
            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Platform
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PLATFORMS.map((platform) => {
                  const IconComponent = platform.icon;
                  const isDisabled = !platform.enabled || isLoading || isGenerating;
                  const isSelected = selectedPlatform === platform.id;
                  
                  return (
                    <button
                      key={platform.id}
                      onClick={() => handlePlatformChange(platform.id)}
                      disabled={isDisabled}
                      className={`p-3 border-2 rounded-lg text-center transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : isDisabled
                          ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                      }`}
                      title={!platform.enabled ? 'Coming soon' : undefined}
                    >
                      <div className="flex justify-center mb-1 text-gray-700">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className={`text-xs font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                        {platform.name}
                      </div>
                      {!platform.enabled && (
                        <div className="text-[10px] text-gray-400 mt-0.5">Coming soon</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Account Selection (if multiple org accounts exist) */}
            {selectedPlatform === 'youtube' && orgAccounts.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select YouTube Account <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  disabled={isLoading || isGenerating || isLoadingAccounts}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {orgAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.display_name || account.platform_account_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select which YouTube channel to upload to
                </p>
              </div>
            )}

            {/* Show account info if single account or parent fallback */}
            {selectedPlatform === 'youtube' && orgAccounts.length === 1 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Uploading to:</strong> {orgAccounts[0].display_name || orgAccounts[0].platform_account_name}
                </p>
              </div>
            )}

            {/* Video Type Selection (only for highlights) */}
            {entityType === 'highlight' && (availableVideoTypes.stitched || availableVideoTypes.enhanced) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Video Version
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setVideoType('stitched')}
                    disabled={!availableVideoTypes.stitched || isLoading || isGenerating}
                    className={`p-3 border-2 rounded-lg text-center transition-colors ${
                      videoType === 'stitched'
                        ? 'border-blue-500 bg-blue-50'
                        : !availableVideoTypes.stitched
                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-700 mb-1">
                      Stitched Video
                    </div>
                    <div className="text-xs text-gray-500">
                      Base video (16:9)
                    </div>
                  </button>
                  <button
                    onClick={() => setVideoType('enhanced')}
                    disabled={!availableVideoTypes.enhanced || isLoading || isGenerating}
                    className={`p-3 border-2 rounded-lg text-center transition-colors ${
                      videoType === 'enhanced'
                        ? 'border-blue-500 bg-blue-50'
                        : !availableVideoTypes.enhanced
                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-700 mb-1">
                      Enhanced Video
                    </div>
                    <div className="text-xs text-gray-500">
                      With overlays (may be 9:16)
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* YouTube Shorts Option (only for YouTube platform) */}
            {selectedPlatform === 'youtube' && (
              <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="isShort"
                  checked={isShort}
                  onChange={(e) => {
                    const newIsShort = e.target.checked;
                    setIsShort(newIsShort);
                    // Update metadata when toggled
                    setMetadata(prev => {
                      let newTitle = prev.title;
                      if (newIsShort) {
                        // Add #Shorts if not already present
                        if (!newTitle.startsWith('#Shorts')) {
                          newTitle = `#Shorts ${newTitle}`;
                        }
                        return {
                          ...prev,
                          title: newTitle,
                          categoryId: null,
                          isShort: true
                        };
                      } else {
                        // Remove #Shorts prefix if present
                        newTitle = newTitle.replace(/^#Shorts\s+/, '');
                        return {
                          ...prev,
                          title: newTitle,
                          categoryId: prev.categoryId || '17', // Default to Sports
                          isShort: false
                        };
                      }
                    });
                  }}
                  disabled={isLoading || isGenerating}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isShort" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Upload as YouTube Short
                </label>
                <span className="text-xs text-gray-500 ml-2">
                  (Video must be ≤60s and 9:16 aspect ratio)
                </span>
              </div>
            )}

            {/* Metadata Form */}
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={metadata.title}
                  onChange={(e) => setMetadata(prev => ({ ...prev, title: e.target.value }))}
                  disabled={isLoading || isGenerating}
                  placeholder="Enter video title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  maxLength={100}
                />
                <p className="text-xs text-gray-500 mt-1">{metadata.title.length}/100 characters</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={metadata.description}
                  onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))}
                  disabled={isLoading || isGenerating}
                  placeholder="Enter video description"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => handleTagsChange(e.target.value)}
                  disabled={isLoading || isGenerating}
                  placeholder="Enter tags separated by commas"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {metadata.tags.length} tag{metadata.tags.length !== 1 ? 's' : ''} added
                </p>
              </div>

              {/* Privacy Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Privacy Setting
                </label>
                <select
                  value={metadata.privacyStatus}
                  onChange={(e) => setMetadata(prev => ({ ...prev, privacyStatus: e.target.value as any }))}
                  disabled={isLoading || isGenerating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sticky Actions Footer */}
          <div className="flex-shrink-0 flex items-center justify-end space-x-3 px-6 pb-6 pt-4 border-t border-gray-200 bg-white sticky bottom-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading || isGenerating}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || isGenerating || !metadata.title.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Uploading...' : isGenerating ? 'Generating...' : 'Upload'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Toast */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast({ ...toast, isVisible: false })}
        />
      )}
    </>
  );
};

export default UploadToSocialMediaModal;

